<template>
  <div class="container" ref="container">
    <div
      class="popover"
      :style="`top: ${tagPopOver.top}px; left: ${tagPopOver.left}px`"
      v-if="tagHover"
    >
      <v-tag
        v-for="tag in tagPopOver.tags"
        :key="tag.id"
        :color="tag.color.colors[0]"
        :backgroundColor="tag.color.colors[1]"
      >
        {{ tag.name }}
      </v-tag>
    </div>
    <header class="recipes-header">
      <div class="recipes-header__main">
        <h1>{{ isRecipesEnabled ? 'Recipes' : 'Ingredients' }}</h1>
        <span>{{ meta.totalCount || 0 }} {{ 'item' | pluralize(meta.totalCount || 0) }}</span>
      </div>
    </header>
    <div class="recipes__actions mt-24">
      <ez-filter-list
        :filters="listFilters"
        @filterUpdated="(...$event) => updateFilters(FilterArea.List, $event)"
        @resetFilter="resetFilters(FilterArea.List)"
        class="mr-16"
      >
        <ez-input
          formKey="filters"
          label="search"
          name="term"
          class="search"
          placeholder="Search for an Item"
        >
          <template #prefix>
            <font-awesome-icon icon="search" />
          </template>
        </ez-input>
      </ez-filter-list>
      <v-filter-dropdown
        :filters="dropdownFilters"
        @filterUpdated="$event => updateFilters(FilterArea.Dropdown, $event)"
        @resetFilters="resetFilters(FilterArea.Dropdown)"
        @filterClosed="filterClosed"
        ref="filtersGroup"
      >
        <template #firstRow>
          <ez-select
            class="input-group product-group-filter"
            name="type"
            :value="dropdownFilters.type"
            :selected="dropdownFilters.type"
            label="Type"
            :options="recipeTypes"
            isFullWidth
          />
        </template>
        <template #thirdRow>
          <ez-tags-filter
            class="tags-filter"
            name="recipeTags"
            :selectedTags="selectedRecipeTags"
            @change="onSelectedTagsChange"
          />
        </template>
      </v-filter-dropdown>

      <ez-simple-dropdown
        class="import-export mr-16"
        placeholder="Import/Export CSV"
      >
        <template #dropdown>
          <ez-button
            type="link"
            formType="button"
            @click="uploadCSVModal"
          >
            Upload CSV
          </ez-button>
          <ez-button
            type="link"
            formType="button"
            @click="exportCSV"
          >
            Export CSV
          </ez-button>
        </template>
      </ez-simple-dropdown>
      <router-link :to="{ name: 'venue-recipe-new' }">
        <ez-button>Add New Item</ez-button>
      </router-link>
    </div>
    <ez-table
      class="mt-24"
      v-if="recipes.length"
      :columns="tableColumns"
      :headers="{
        targetCogsAmount: () => 'Target COGS ($)',
        targetCogsPercentage: () => 'Target COGS (%)',
        isHidden: () => 'Visibility',
        tags: () => 'Tags',
      }"
      :columnProps="{
        targetCogsAmount: { class: 'price-cell' },
        targetCogsPercentage: { class: 'price-cell' },
        salesPrice: { class: 'price-cell' },
        isHidden: { class: 'visibility-cell' },
        type: { class: 'medium-cell' },
        unit: { class: 'medium-cell' },
      }"
      :data="recipes"
      :has-min-height="false"
      :column-props="{}"
      @rowClick="openSingleItem"
    >
      <template #cell-name="{ row }">
        <ez-entity-info :imgUrl="row.image">
          <div class="recipe-info">
            <div class="recipe-info__name" :title="row.name">{{ row.name }}</div>
          </div>
        </ez-entity-info>
      </template>
      <template #cell-type="{ row }">
        {{
          row.type === 'recipe' ? 'Recipe' : row.type === 'ingredient' ? 'Ingredient' : 'Menu Item'
        }}
      </template>
      <template #cell-unit="{ row }">
        {{ row.unit?.name || '-' }}
      </template>
      <template #cell-tags="{ row }">
        <div
          class="tags"
          v-if="row.tags && row.tags.length"
          @mouseenter="tagsShowHover(row.id, row.tags)"
          @mouseleave="tagsRemoveHover"
          :ref="`tag-${row.id}`"
        >
          <v-tag
            class="mr-8"
            :color="row.tags[0].color.colors[0]"
            :backgroundColor="row.tags[0].color.colors[1]"
          >{{ row.tags[0].name }}</v-tag>
          <span v-if="row.tags.length > 1">+{{ row.tags.length - 1 }}</span>
        </div>
        <span v-else>-</span>
      </template>
      <template #cell-isHidden="{ row }">
        <div v-if="groupViewMode && row.showVisibility" @click.stop="openLocationModal(row)">
          <v-location-badge v-if="row.visibleForAllFromGroup" type="green">All</v-location-badge>
          <v-location-badge v-else-if="row.visibleForVenueIdsFromGroup?.length" type="yellow"
            >Some</v-location-badge
          >
          <v-location-badge v-else type="red">None</v-location-badge>
          <font-awesome-icon icon="pen" class="location-edit"></font-awesome-icon>
        </div>
        <status-badge
          v-else-if="row.showVisibility"
          :status="row.isHidden ? 'hidden' : 'visible'"
        />
        <span v-else>-</span>
      </template>
      <template #cell-salesPrice="{ row }">
        <v-price v-if="row.salesPrice !== null" :price="row.salesPrice || 0" />
        <span v-else>-</span>
      </template>
      <template #cell-targetCogsAmount="{ row }">
        <v-price :price="row.targetCogsAmount || 0" />
      </template>
      <template #cell-targetCogsPercentage="{ row }">
        {{ row?.targetCogsPercent ? `${row.targetCogsPercent}%` : '-' }}
      </template>
    </ez-table>
    <empty-state class="mt-24" v-else>
      <template #badge><img src="@/assets/no-products-search-state.svg" alt="" /></template>
      <template #title>No recipes to display</template>
    </empty-state>
    <div v-if="isLoadingMore" class="u-text-center mt-12">
      <ez-spinner />
    </div>
    <ez-loader :show="isLoading">Loading...</ez-loader>
    <ez-load-more
      v-if="meta.lastPage > meta.page && !isLoadingMore && !isLoading"
      @loadMore="onLoadMoreClick"
    ></ez-load-more>
    <select-locations-modal
      ref="locationsModal"
      :locations="selectedItemsLocation"
      :recipeId="itemId"
      @success="updateRecipeLocations"
      @close="onRecipeLocationsClose"
    />
    <ez-csv-upload-modal
      ref="csvUploadModal"
      :uploadCSV="uploadCSVAction"
      :onUploadFinished="onUploadFinished"
      venue-recipes
      :template-url="`venue/recipes/import-template`"
    />
    <ez-drawer ref="singleItemDrawer" class="item-drawer" @onClose="resetDrawerRoute">
      <template #title
        ><v-badge class="title-badge">{{ titleCase(selectedItem?.type) }}</v-badge></template
      >
      <template #content>
        <div class="content">
          <div class="info-section">
            <div>
              <h3>{{ selectedItem?.name }}</h3>
              <v-price v-if="selectedItem?.salesPrice" :price="selectedItem.salesPrice || 0" :unit="selectedItem.unit?.name" flex-row/>
              <div v-else class="v-price--row">
                <span class="v-price__unit--row">
                  {{ formatUnitDisplay(selectedItem?.quantity, selectedItem?.unit?.name) }}
                </span>
              </div>
            </div>
            <div class="info-section__image">
              <img
                v-if="selectedItem?.image"
                :src="selectedItem?.image"
                alt="Recipe Image"
                class="recipe-image"
              />
            </div>
          </div>

          <div v-if="selectedItem?.tags?.length" class="tags-section mt-16">
            <span class="tags-section__label">Tags</span>
            <div class="tags-section__display">
              <v-tag
                v-for="tag in selectedItem.tags"
                :key="tag.id"
                :color="tag.color.colors[0]"
                :backgroundColor="tag.color.colors[1]"
              >
                {{ tag.name }}
              </v-tag>
            </div>
          </div>

          <hr />
          <ez-table
            :columns="['name', 'quantity', 'cogs']"
            :data="selectedItem?.recipeItems || []"
            :headers="{
              name: () => 'Item',
              quantity: () => selectedItem?.type === 'ingredient' ? 'Conversion' : 'Qty',
              cogs: () => 'COGS',
            }"
            :column-props="{
              cogs: { class: 'price-cell' },
              quantity: { class: 'quantity-cell medium-cell' },
            }"
            @rowClick="openSingleRecipeItem"
          >
            <template #cell-name="{ row }">
              <ez-entity-info :imgUrl="row.item.image">
                <template>
                  <div class="recipe-item">
                    <span class="recipe-item__name">{{ row.item?.name }}</span>
                    <span class="recipe-item__unit">{{ row.item.unit?.name }}</span>
                  </div>
                </template>
              </ez-entity-info>
            </template>
            <template #cell-quantity="{ row }">
              <div class="quantity-cell__content">
                <span>{{ row.quantity }}</span>
                <span>{{ row.unit?.label }}</span>
              </div>
            </template>
            <template #cell-cogs="{ row }">
              <div class="cogs-cell__content">
                <v-price :price="row.targetCogsAmount || 0" />
                <span v-if="row.targetCogsPercent !== null" class="percent-cell">{{
                    row.targetCogsPercent
                  }}%</span>
                <span v-if="row.targetCogsPercent === null" class="percent-cell">/ {{
                    row.unit?.label
                  }}</span>
              </div>
            </template>
          </ez-table>
        </div>
      </template>

      <template #footer>
        <div class="recipe-footer">
          <div class="recipe-footer__buttons">
            <ez-button @click="editItem">Edit Recipe</ez-button>
            <ez-button @click="openDeleteModal" class="ml-8" type="secondary">Delete Recipe</ez-button>
          </div>
          <div v-if="selectedItem" class="recipe-footer__total">
            <v-recipe-total-info
              v-if="selectedItem.salesPrice !== null"
              :sales-price="selectedItem.salesPrice"
              :target-cogs-amount="selectedItem.targetCogsAmount"
              :target-cogs-percent="selectedItem.targetCogsPercent"
              show-info-icon
            />
            <v-recipe-total-info
              v-else
              :target-cogs-amount="selectedItem.targetCogsAmount"
              :show-info-icon="false"
            />
          </div>
        </div>
      </template>
    </ez-drawer>
    <ez-confirmation-modal ref="deleteRecipeModal" type="red">
      <template #title>Delete Recipe?</template>
      <template #content>
        <p>This recipe will be permanently deleted. This action cannot be undone.</p>
      </template>
      <template #footer>
        <ez-button @click="closeDeleteModal" type="link">Cancel</ez-button>
        <ez-button @click="confirmDeleteRecipe" type="red">Delete Recipe</ez-button>
      </template>
    </ez-confirmation-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { debounce, clone } from '@/util/utils';
import flash from '@/components/ui/FlashMessage';
import EzFilterList from '@/components/ui/FilterList';
import EzInput from '@/components/ui/Input';
import EzSelect from '@/components/ui/Select';
import VFilterDropdown from '@/components/v3/patterns/VFilterDropdown';
import EzTable from '@/components/ui/Table';
import EzEntityInfo from '@/components/ui/EntityInfo';
import { LOADING_KEY, TagType } from '@/util/constants';
import EzSpinner from '@/components/ui/Spinner/EzSpinner';
import EzLoader from '@/components/ui/Loader/EzLoader';
import EzLoadMore from '@/components/ui/LoadMore';
import EmptyState from '@/views/common/empty-state';
import EzButton from '@/components/ui/Button';
import VPrice from '@/components/v3/elements/VPrice';
import StatusBadge from '@/views/common/status-badge/StatusBadge.vue';
import VLocationBadge from '@/components/v3/elements/VLocationBadge';
import SelectLocationsModal from '@/views/platform/venue/suppliers/all/SelectLocationsModal.vue';
import EzDrawer from '@/components/ui/Drawer';
import { titleCase } from 'change-case';
import VBadge from '@/components/v3/elements/VBadge';
import VRecipeTotalInfo from '@/components/v3/patterns/VRecipeTotalInfo';
import EzTagsFilter from '@/components/ui/Filter/Tags.vue';
import { EzConfirmationModal, EzCsvUploadModal } from '@/components/ui/Modal';
import VTag from '@/components/v3/elements/VTag';
import EzSimpleDropdown from '@/components/ui/Dropdown/EzSimpleDropdown';
import downloadAttachment from '@/util/downloadAttachment';

const FilterArea = {
  List: 'list',
  Dropdown: 'dropdown',
};

export default {
  components: {
    EzFilterList,
    VFilterDropdown,
    VRecipeTotalInfo,
    EzInput,
    EzSelect,
    EzTable,
    EzEntityInfo,
    EzSpinner,
    EzLoader,
    EzLoadMore,
    EmptyState,
    EzButton,
    VPrice,
    StatusBadge,
    VLocationBadge,
    SelectLocationsModal,
    EzDrawer,
    VBadge,
    EzTagsFilter,
    EzConfirmationModal,
    VTag,
    EzCsvUploadModal,
    EzSimpleDropdown,
  },
  name: 'VenueRecipesIndex',
  props: {
    id: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      meta: {},
      listFilters: {
        term: null,
      },
      dropdownFilters: {},
      recipes: [],
      recipeTypes: [],
      FilterArea,
      selectedItemsLocation: [],
      itemId: null,
      selectedItem: null,
      titleCase,
      selectedRecipeTags: [],
      appliedRecipeTags: [],
      tagHover: false,
      tagPopOver: {
        tags: [],
        top: null,
        left: null,
      },
    };
  },
  computed: {
    ...mapGetters('loading', ['getLoading']),
    ...mapGetters('entities/users', ['getVenues']),
    ...mapState('entities/users', ['loggedUser', 'context']),
    tableColumns() {
      return [
        'name',
        'unit',
        'type',
        'tags',
        ...(this.groupViewMode ? ['isHidden'] : []),
        'salesPrice',
        'targetCogsAmount',
        'targetCogsPercentage',
      ];
    },
    isLoading() {
      return this.getLoading(LOADING_KEY.VENUE_FETCH_RECIPES);
    },
    isLoadingMore() {
      return this.getLoading(LOADING_KEY.VENUE_FETCH_MORE_RECIPES);
    },
    groupViewMode() {
      if (this.loggedUser.context === 'venue-group') return true;
      return false;
    },
    venues() {
      return this.getVenues;
    },
    isRecipesEnabled() {
      return this.context?.features?.recipeManagement || false;
    },
  },
  methods: {
    ...mapActions('recipes', ['venueFetchRecipes', 'venueFetchRecipe', 'venueDeleteRecipe', 'venueFetchRecipeTypes', 'venueUploadRecipeCSV', 'venueExportRecipeCSV']),
    ...mapActions('tags', ['venueFetchTags', 'fetchTagColors']),
    ...mapMutations('recipes', ['UPDATE_DRAFT_RECIPE']),
    ...mapMutations('tags', ['CLEAR_ALL_TAGS', 'SET_SELECTED_TAGS']),
    formatUnitDisplay(quantity, unitName) {
      if (!unitName) return '';
      const qty = quantity || 1;
      if (qty === 1) {
        return unitName;
      }
      return `${qty} ${unitName}(s)`;
    },
    getTooltipContent() {
      if (!this.selectedItem) return '';

      const salesPrice = this.selectedItem.salesPrice || 0;
      const targetCogsAmount = this.selectedItem.targetCogsAmount || 0;
      const targetCogsPercent = this.selectedItem.targetCogsPercent || 0;

      return `Sales Price: $${salesPrice.toFixed(2)}\nTarget COGS($): $${targetCogsAmount.toFixed(2)}\nTarget COGS(%): ${targetCogsPercent}%`;
    },

    async getRecipeTypesForFilter() {
      const ALL_FILTER_OPTION = { id: null, name: 'All' };
      try {
        const { data } = await this.venueFetchRecipeTypes();
        const apiTypes = data.data || [];
        this.recipeTypes = [
          ALL_FILTER_OPTION,
          ...apiTypes
        ];
      } catch (error) {
        this.recipeTypes = [ALL_FILTER_OPTION];
      }
    },
    async fetchRecipes(filters = {}, loadingKey = LOADING_KEY.VENUE_FETCH_RECIPES) {
      try {
        const {data} = await this.venueFetchRecipes({
          filters,
          loadingKey,
          ...this.meta?.page && {
            page: this.meta?.page + 1,
          },
        });
        this.meta = data.meta;
        if (loadingKey === LOADING_KEY.VENUE_FETCH_MORE_RECIPES) {
          this.recipes = [...this.recipes, ...data.data];
        } else {
          this.recipes = data.data;
        }
      } catch (e) {
        flash.error({
          title: 'Something went wrong',
          message: e.response?.data?.error?.message || '',
        });
      }
    },
    async onLoadMoreClick() {
      await this.fetchRecipes(
        {
          ...this.listFilters,
          ...this.dropdownFilters,
        },
        LOADING_KEY.VENUE_FETCH_MORE_RECIPES,
      );
    },
    updateFilters: debounce(async function deb(area, event) {
      if (area === FilterArea.List) {
        this.listFilters = {
          term: event[1],
        };
      } else if (area === FilterArea.Dropdown) {
        const tagIds = this.selectedRecipeTags.map(item => item.id);
        this.dropdownFilters = {
          ...this.dropdownFilters,
          ...(typeof event.type === 'object' && event.type !== null && { type: event.type.id }),
          ...(tagIds.length ? { tagIds } : { tagIds: null }),
        };
        this.appliedRecipeTags = clone(this.selectedRecipeTags);
        this.$nextTick(() => this.$refs.filtersGroup.closeFilterDropdown());
      }
      this.meta = {};
      await this.fetchRecipes({
        ...this.listFilters,
        ...this.dropdownFilters,
      });
    }, 300),
    async resetFilters(area) {
      if (area === FilterArea.List) {
        this.listFilters.term = null;
      } else if (area === FilterArea.Dropdown) {
        this.selectedRecipeTags = [];
        this.appliedRecipeTags = [];
        this.dropdownFilters = {};
      }
      this.$nextTick(() => this.$refs.filtersGroup.closeFilterDropdown());
      this.meta = {};
      await this.fetchRecipes({
        ...this.listFilters,
        ...this.dropdownFilters,
      });
    },
    onSelectedTagsChange(tags) {
      this.selectedRecipeTags = tags;
    },
    filterClosed() {
      this.selectedRecipeTags = clone(this.appliedRecipeTags);
    },
    tagsShowHover(id, tags) {
      this.tagHover = true;
      this.tagPopOver.tags = tags;
      const { top, left, bottom } = this.$refs[`tag-${id}`].getBoundingClientRect();
      const topParent = this.$refs.container?.getBoundingClientRect().top || 0;
      const leftParent = this.$refs.container?.getBoundingClientRect().left || 0;
      this.tagPopOver.top = top - topParent + (bottom - top);
      this.tagPopOver.left = left - leftParent;
    },
    tagsRemoveHover() {
      this.tagHover = false;
      this.tagPopOver = {
        tags: [],
        top: null,
        left: null,
      };
    },
    openLocationModal(item) {
      this.selectedItemsLocation = item.visibleForVenueIdsFromGroup;
      this.itemId = item.id;
      this.$refs.locationsModal.open();
    },
    updateRecipeLocations(val) {
      const index = this.recipes.findIndex(item => item.id === this.itemId);
      if (index !== -1) {
        this.recipes[index].visibleForVenueIdsFromGroup = val;
        if (val.length === this.venues.length) {
          this.recipes[index].visibleForAllFromGroup = true;
        } else {
          this.recipes[index].visibleForAllFromGroup = false;
        }
      }
    },
    onRecipeLocationsClose() {
      this.selectedItemsLocation = [];
      this.itemId = null;
    },
    async loadItemDrawer({ id }) {
      try {
        const { data } = await this.venueFetchRecipe({ id });
        this.selectedItem = data.data;
        this.openItemDrawer();
      } catch (e) {
        this.resetDrawerRoute();
      }
    },
    async openSingleItem(row) {
      try {
        const { data } = await this.venueFetchRecipe({ id: row.id });
        this.selectedItem = data.data;

        this.$router.push({
          name: 'venue-recipes',
          params: { id: row.id },
        });

        this.openItemDrawer();
      } catch (e) {
        this.resetDrawerRoute();
      }
    },
    resetDrawerRoute() {
      this.$router.replace({ name: 'venue-recipes' });
      this.selectedItem = null;
    },
    openItemDrawer() {
      this.$refs.singleItemDrawer.open();
    },
    openDeleteModal() {
      this.$refs.deleteRecipeModal.open();
    },
    closeDeleteModal() {
      this.$refs.deleteRecipeModal.close();
    },
    async confirmDeleteRecipe() {
      try {
        await this.venueDeleteRecipe({ id: this.selectedItem.id });
        this.closeDeleteModal();
        flash.success({
          title: 'Recipe deleted successfully!',
        });
        this.$refs.singleItemDrawer.close();
        this.meta = {};
        await this.fetchRecipes({
          ...this.listFilters,
          ...this.dropdownFilters,
        });
      } catch (e) {
        flash.error({
          title: 'Something went wrong.',
          message: e.response?.data?.error?.message || '',
        });
      }
    },
    editItem() {
      this.UPDATE_DRAFT_RECIPE({
        ...this.selectedItem,
        unitId: this.selectedItem.unit?.id || null,
        items: this.selectedItem.recipeItems.map(item => ({
          ...item,
          ...item.item,
        })),
      });
      this.$refs.singleItemDrawer.close();
      this.$router.push({ name: 'venue-recipe-new' });
    },
    openSingleRecipeItem(row) {
      const routerData = row.recipeId
        ? this.$router.resolve({
            name: 'venue-recipes',
            params: { id: row.recipeId },
          })
        : this.$router.resolve({
            name: 'venue-orders-products',
            params: { id: row.productId },
          });
      window.open(routerData.href, '_blank');
    },
    uploadCSVModal() {
      this.$refs.csvUploadModal.open();
    },
    uploadCSVAction(data) {
      return this.venueUploadRecipeCSV({
        data,
      });
    },
    onUploadFinished() {
      this.meta = {};
      this.fetchRecipes({
        ...this.listFilters,
        ...this.dropdownFilters,
      });
    },
    async exportCSV() {
      const params = {
        ...this.listFilters,
        ...this.dropdownFilters,
      };

      const { data, headers } = await this.venueExportRecipeCSV({
        params
      });
      const filename = (headers['content-disposition'] || '').match(/filename="(.+)"/);
      const name = Array.isArray(filename) ? decodeURI(filename[1]) : 'Recipes.xlsx';
      downloadAttachment(data, name);
    },
  },
  async created() {
    await this.getRecipeTypesForFilter();
    await Promise.all([
      this.venueFetchTags({ query: { type: TagType.Recipe } }),
      this.fetchTagColors(),
    ]);
    this.SET_SELECTED_TAGS([]);
    this.meta = {};
    await this.fetchRecipes({
      ...this.listFilters,
      ...this.dropdownFilters,
    });
    if (this.id) await this.loadItemDrawer({ id: this.id });
  },
};
</script>

<style lang="scss" scoped>

.container {
  position: relative;
}

.recipe-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__buttons {
    display: flex;
    align-items: center;
  }

  &__total {
    margin-left: auto;
  }
}

.recipes-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  &__main {
    h1 {
      @include font-size(24px);
      margin: 0;
      font-weight: 500;
    }
    > span {
      @include font-size(12px);
      color: $color-gray-6C;
    }
  }
}
.recipes__actions {
  display: flex;

  :deep() .dropdown-filters {
    .input-group {
      margin: 0;
    }
    .filters-area {
      hr {
        margin-bottom: 12px;
      }
    }
    .filters-area__row:not(:first-child) {
      justify-content: start;
      grid-template-columns: auto auto auto auto auto;
    }
  }

  .tags-filter {
    :deep() .tags-wrapper {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: none;
    }

    :deep() .tags-wrapper__selected {
      padding-bottom: 22px;
    }

    :deep() .input-group.search {
      display: flex;
      align-items: center;
      margin: 22px 0;
      font-weight: 600;
    }

    :deep() .input-group__label {
      margin-bottom: 0;
      margin-right: 16px;
    }
  }

  :deep() .ez-filter-list {
    &__items {
      margin: 0;
    }
  }
  :deep() .ez-filter-list .ez-filter-list__items .product-group-filter .select-search__trigger {
    min-width: 172px;
  }

  :deep() .ez-filter-list .ez-filter-list__items .select-search__wrapper {
    .select-search__dropdown,
    .select-search__dropdown.select-search__dropdown--width-100 {
      min-width: 100%;
      width: max-content;
      color: $color-gray-25;
    }
  }

  .recipes__actions-cta {
    margin-left: auto;
  }
}

.import-export {
  width: auto;
  margin-left: auto;

  :deep() .ez-simple-dropdown {
    &__label {
      margin-right: 16px;
    }

    &__display-container--active {
      background-color: $color-gray-E9;
    }

    &__dropdown {
      width: max-content;
      overflow: visible;
    }
  }

  .button--link {
    width: 100%;
    padding-left: 0.625rem;
    padding-right: 0.625rem;
    text-align: left;
    color: $color-gray-25;
    font-weight: 400;

    &:hover {
      background: $color-gray-F5;
    }

    &:disabled {
      color: $color-gray-6C;
      background-color: transparent;
    }
  }
}

:deep() .ez-empty-state__image {
  max-width: none;
}

:deep() .table {
  .visibility-cell {
    width: 100px;
    overflow: visible;

    .button {
      padding-left: 0;
    }
    .location-edit {
      display: none;
    }

    &:hover {
      cursor: pointer;
      .location-edit {
        display: inline-block;
      }
      .location-badge {
        display: none;
      }
    }
  }

  .entity-info {
    &__text {
      overflow-x: visible;
      .recipe-item {
        &__name {
          display: block;
          font-weight: 500;
          line-height: 1.2;
        }

        &__unit {
          display: block;
          font-size: 0.75rem;
          color: $color-gray-6C;
          margin-top: 2px;
          line-height: 1;
        }
      }
    }
  }

}

:deep() .ez-select__display-container {
  height: 2.25rem;
}

.item-drawer {
  :deep() .drawer__footer {
    border-top: 1px solid #eceef5;
    text-align: left;
  }

  .content {
    padding: 0 24px;

    .info-section {
      display: flex;
      justify-content: space-between;

      :deep() .v-price {
        &--row {
          .v-price {
            &__price {
              font-size: 18px;
              letter-spacing: -0.5px;
              line-height: 24px;
              font-weight: 500;
            }
            &__unit--row {
              color: $color-gray-6C;
              margin-top: 8px;
              font-size: 0.875rem;
              line-height: 22px;
              font-weight: 400;
            }
          }
        }
      }

      h3 {
        margin: 0;
        font-size: 24px;
        line-height: 32px;
        font-weight: 500;
      }

      &__image {
        img {
          max-width: 168px;
          max-height: 168px;
        }
      }
    }

    :deep() .table {
      .quantity-cell .quantity-cell__content {
        display: flex;
        flex-direction: column;
      }

      .price-cell .cogs-cell__content {
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        .light-gray {
          color: $color-gray-6C;
        }
      }
      .percent-cell {
        @extend %text-overflow-ellipsis;
        color: $color-gray-6C;
        font-size: 12px;
        line-height: 14px;
        font-weight: normal;
        letter-spacing: -0.25px;
        &.has-tooltip {
          display: block;
        }
      }
    }

    .tags-section {
      &__label {
        display: block;
        font-size: 12px;
        line-height: 18px;
        color: $color-gray-6C;
        margin-bottom: 8px;
        font-weight: 400;
      }

      &__display {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
    }
  }
}

.popover {
  position: absolute;
  background: white;
  border: 1px solid #e1e5ed;
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  max-width: 300px;
}

.tags {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
</style>
